/**
 * @fileoverview The share studio's pictures, made while the gardener is still
 * choosing (both platforms open a share sheet only while the tap is live):
 * the set for the choices as they stand (buildShareFrames), only what changed
 * drawn again (frameCache), a season's card made by the season review, the
 * "Preparing" line, and the finished picture in view (or the photograph
 * standing in for it). A set that could not be made is reported to Sentry
 * with what it was, and a failure on our side says the standard sentence
 * (lib/serverFailure). Moved out of
 * plant-list/components/dialogs/SharePlantDialog (batch 18, A105); nothing in
 * it changed.
 *
 * Where: SharePlantDialog.
 * API: the photo files through buildShareFrames (features/share/frames).
 * Settings: SHARE_PREPARING_DELAY_MS, SHARE_REBUILD_DELAY_MS (config/tunables/plantList).
 * Words (en/es): plants.share.failed, locations.card.plants.count*.
 *
 * @module useShareFrames
 */

import React from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import type { PlantPhoto } from '@/shared/types/photo';
import { buildShareFrames, cachedShareFrames } from '@/features/share/frames/buildFrames';
import type { PhotoMeta, ShareFrameInput } from '@/features/share/frames/frameOptions';
import { slotAspectOf as slotAspectOfInput } from '@/features/share/frames/frameLayouts';
import { plantSubject } from '@/shared/api/subject';
import { SHARE_PREPARING_DELAY_MS, SHARE_REBUILD_DELAY_MS } from '@/config/tunables/plantList';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import { ELEMENT_KEYS, type BuiltFrames, type SeasonCardChoices, type SharePlantDialogProps } from './shareStudioTypes';
import type { ShareStory } from './useShareStory';

/**
 * Reports a set of pictures that could not be made, whatever the reason
 * (owner, 2026-10-13: "The pictures could not be prepared" seen on a phone
 * and not reproduced): the server's answer (status and code) and the photo
 * request it was for, or the drawing's own error, with what the set was.
 * A failure on our side is also reported as one (reportServerFailure).
 *
 * @param err - What building the set threw.
 * @param what - The set: its layout, size and how many photographs.
 */
const reportFramesFailed = (err: unknown, what: { layout: string; size: string; photos: number }): void => {
  if (isServerFailure(err)) {
    reportServerFailure(err, 'share.frames');
    return;
  }
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  const body = axios.isAxiosError(err) ? (err.response?.data as { code?: unknown; error?: unknown } | undefined) : undefined;
  const url = axios.isAxiosError(err) ? String(err.config?.url ?? '').split('?')[0] : '';
  Sentry.withScope((scope) => {
    scope.setTag('where', 'share.frames');
    scope.setTag('status', status === undefined ? 'none' : String(status));
    scope.setExtras({ ...what, url, code: body?.code ?? null, serverError: body?.error ?? null });
    // One issue per status (or drawing failure), not one per photograph.
    scope.setFingerprint(['share-frames-failed', status === undefined ? 'drawing' : String(status)]);
    Sentry.captureException(err instanceof Error ? err : new Error('Share pictures could not be made'));
  });
};

/** What useShareFrames hands the dialog. */
export interface ShareFrames {
  /** The last set finished, kept on screen while the next is made. */
  built: BuiltFrames | null;
  /** A set that could not be made, and why. */
  buildError: { key: string; message: string } | null;
  /** How far the set being drawn has got, in pictures; null when nothing is drawn. */
  progress: { done: number; total: number } | null;
  /** The pictures for the choices as they stand; null while they are made. */
  frames: File[] | null;
  /** The set for these choices could not be made. */
  failed: boolean;
  /** Pictures are being drawn, not only waited for. */
  drawing: boolean;
  /** The preview says "Preparing" (the drawing has gone on a while). */
  preparingShown: boolean;
  /** An object URL for the finished picture in view. */
  previewUrl: string | null;
  /** What the preview shows: the finished picture, else the photograph standing in. */
  previewSrc: string | null;
  /** The shape (width over height) of the slot a photograph fills in the size and layout chosen: the crop box's (B86). */
  slotAspectOf: (photo: PlantPhoto) => number;
}

/**
 * The pictures for the sharer's choices, made ahead of the tap that shares them.
 *
 * @param props - The dialog's props (SharePlantDialogProps).
 * @param story - The story and the choices (useShareStory).
 * @returns The finished set (or null while it is made), whether it failed, the progress, and the preview's picture.
 */
export function useShareFrames(props: SharePlantDialogProps, story: ShareStory): ShareFrames {
  const { isOpen, plantObjId, name, variety, plantOf, plantCount = 0, season } = props;
  const {
    subject, isLocation, t, address, style, dateDay, dateYear, cardPhoto, coverElements, plantElements, shownPhoto,
    shownIsCard, cardIn, layout, set, peekPhoto, order, selectionKey, collageIn, shownIsCollage, nothingToSend,
    wordsFor, wordsKey, focusFor, focusKey, noteShape, handle, handleKey, formatOne, formatSpan,
  } = story;

  /** The last set of pictures finished, kept on screen while the next is made. */
  const [built, setBuilt] = React.useState<BuiltFrames | null>(null);
  /** A set that could not be made, and why: shown until the choices change. */
  const [buildError, setBuildError] = React.useState<{ key: string; message: string } | null>(null);
  /** How far the set being drawn has got, in pictures: null when nothing is being drawn. */
  const [progress, setProgress] = React.useState<{ done: number; total: number } | null>(null);

  /*
   * Compose while the gardener is still choosing.
   *
   * Both platforms will only open a share sheet while the tap that asked for
   * it is still live, and ten frames take long enough that the permission has
   * lapsed by the time they are ready. So the work happens here, and the tap
   * itself does nothing but hand over files that already exist.
   */
  /** What is printed, as one key (a location's two sets as well). */
  const elementsKey = ELEMENT_KEYS.map((k) => (style.elements[k] ? '1' : '0')).join('')
    + (isLocation ? `|${JSON.stringify(coverElements)}|${JSON.stringify(plantElements)}` : '');
  /** "4 plants", for a location's cover. */
  const countText = plantCount === 1 ? t('locations.card.plants.count', { count: 1 }) : t('locations.card.plants.count_plural', { count: plantCount });
  /**
   * What each photograph prints, in a location's share: a plant's photo its plant's, the rest the location's.
   * In a season's, each photo prints the share's switches with its own plant's name.
   *
   * @param photo - The photograph.
   * @returns Its switches and names, or null in a plant's share (the share's own apply).
   */
  const photoMeta = React.useCallback((photo: PlantPhoto): PhotoMeta | null => {
    if (season) {
      const plant = photo.photo_id < 0 ? plantOf?.(photo.photo_id) ?? null : null;
      return plant ? { elements: { ...style.elements, words: true }, name: plant.name, variety: plant.variety } : null;
    }
    if (!isLocation) return null;
    const plant = photo.photo_id < 0 ? plantOf?.(photo.photo_id) ?? null : null;
    return plant
      ? { elements: { ...plantElements, words: true }, name: plant.name, variety: plant.variety, place: name }
      : { elements: { ...coverElements, words: true }, name, variety: name, count: plantCount > 0 ? countText : null };
  }, [season, isLocation, plantOf, style.elements, plantElements, coverElements, name, plantCount, countText]);
  /**
   * Where a plant's photo in a location's or a season's share is read from: its plant.
   *
   * @param photoId - The photograph's id (negative for a plant's photo in those shares).
   * @returns The plant and the photo's own id, or null to read it from the share's subject.
   */
  const photoSource = React.useCallback((photoId: number) => {
    if ((!isLocation && !season) || photoId >= 0) return null;
    const plant = plantOf?.(photoId);
    return plant ? { subject: plantSubject(plant.plantObjId), photoId: -photoId } : null;
  }, [isLocation, season, plantOf]);
  /*
   * Everything the pictures are made from, as one key. The photograph in view
   * is not part of it: every layout makes a picture of each photograph going
   * out (Before & after one of the first and last), so looking at another one
   * is choosing a finished picture, not making the set again. It used to be,
   * and every tap in the strip made the whole set over.
   */
  // Whether the Grow mark heads the words: not on an account the Grow settings leave it off.
  // No Grow mark on any picture (2026-10-11): the signature is the gardener's own address (SHARE_SIGNATURE).
  const showMark = false;
  const buildKey = [
    selectionKey, layout, style.size, elementsKey, wordsKey, focusKey, handleKey,
    style.cardGround, style.textScale, plantObjId, subject.kind, subject.id, name, variety, showMark, address,
    season?.cardKey ?? '', `${dateDay ? 'd' : ''}${dateYear ? 'y' : ''}`,
  ].join('|');
  /** The pictures for the choices as they stand: none while they are being made. */
  const frames = built?.key === buildKey ? built.files : null;
  /** The set for these choices could not be made. */
  const failed = buildError?.key === buildKey;
  /** The pictures are being made again; the last set stays on screen meanwhile. */
  const building = isOpen && !nothingToSend && !frames && !failed;
  /*
   * Pictures are being drawn, not only waited for: what "Preparing" says.
   * Not during the pause for the taps to stop, nor when every picture was
   * made already (a photograph tapped out of the story needs none), so it
   * shows only for real work, and briefly: one picture for a photograph
   * tapped in.
   */
  const drawing = building && progress !== null && progress.total > 0;
  /**
   * Whether the preview says "Preparing": only once the drawing has gone on
   * for about a second (SHARE_PREPARING_DELAY_MS), so a set made quickly
   * says nothing and a tap is not answered with a flash of words. The
   * picture dims the moment drawing starts, whatever this says.
   */
  const [preparingShown, setPreparingShown] = React.useState(false);
  React.useEffect(() => {
    if (!drawing) {
      setPreparingShown(false);
      return;
    }
    const timer = setTimeout(() => setPreparingShown(true), SHARE_PREPARING_DELAY_MS);
    return () => clearTimeout(timer);
  }, [drawing]);
  /** The last set's key, read by the build without making it a reason to build. */
  const builtKey = React.useRef<string | null>(null);
  builtKey.current = built?.key ?? null;
  /**
   * Pictures made while the sheet is open, by what each was made from, so a
   * set made again draws only what changed (see buildShareFrames).
   */
  const frameCache = React.useRef<Map<string, File>>(new Map());
  /** A season's cards made while the sheet is open, by what each was made from. */
  const cardCache = React.useRef<Map<string, File>>(new Map());
  React.useEffect(() => {
    if (!isOpen) {
      setBuilt(null);
      setBuildError(null);
      frameCache.current.clear();
      cardCache.current.clear();
    }
  }, [isOpen]);
  /**
   * What every picture in the set shares; each picture's own photograph, line and crop are added to it.
   * The date's Day and Year are in it: without them a picture made before a chip was tapped came back
   * with its old date (owner, 2026-10-14: "The day shows when it's turned off").
   */
  const cacheKey = [
    layout, style.size, elementsKey, handleKey, style.cardGround, style.textScale,
    plantObjId, subject.kind, subject.id, name, variety, showMark, address, `${dateDay ? 'd' : ''}${dateYear ? 'y' : ''}`,
  ].join('|');
  /**
   * The sharer's choices as the pictures take them (before the cache and the progress are added):
   * what the build is given, and what the crop screen asks the slot's shape of (slotAspectOf).
   *
   * @returns The choices as they stand.
   */
  const frameInput = (): ShareFrameInput => ({
    plantObjId, subject, name, variety, photos: set, peekPhoto,
    size: style.size, layout, elements: { ...style.elements, words: true }, cardGround: style.cardGround,
    textScale: style.textScale,
    // The builder does not read the lead; it is sent as it always was.
    wordsFor, focusFor, lead: shownPhoto, handle, showMark, address,
    formatDate: formatOne, formatSpan, photoMeta, photoSource, onPhotoShape: noteShape,
  });
  /**
   * The shape (width over height) of the slot a photograph fills in the picture the size and layout
   * chosen make: what its crop box must be (B86).
   *
   * @param photo - The photograph being cropped.
   * @returns The slot's width over its height.
   */
  const slotAspectOf = (photo: PlantPhoto): number => slotAspectOfInput(frameInput(), photo);
  React.useEffect(() => {
    // Back where the last set was made (a photograph tapped in and out again): nothing to make.
    if (!isOpen || nothingToSend || builtKey.current === buildKey) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    const madeFrom = { key: buildKey, order, layout, cardIn, collageIn };
    const input: ShareFrameInput = {
      ...frameInput(),
      frameCache: frameCache.current, cacheKey,
      onProgress: (done, total) => { if (!cancelled) setProgress({ done, total }); },
      isCancelled: () => cancelled,
    };
    // A season's card: what it is made from, and the card if it was made already.
    const cardChoices: SeasonCardChoices | null = season && cardIn && cardPhoto
      ? {
        size: style.size,
        words: wordsFor(cardPhoto),
        handle: style.elements.handle ? handle : null,
        photos: set.flatMap((photo) => {
          const plant = photo.photo_id < 0 ? plantOf?.(photo.photo_id) : null;
          return plant ? [{ plantObjId: plant.plantObjId, photoId: -photo.photo_id }] : [];
        }),
      }
      : null;
    const cardKey = cardChoices && season ? `${season.cardKey}|${JSON.stringify(cardChoices)}` : '';
    const readyCard = cardChoices ? cardCache.current.get(cardKey) ?? null : null;
    /**
     * The card for these choices: the one made already, or made now and kept.
     *
     * @returns The card, or null when none goes out.
     */
    const makeCard = async (): Promise<File | null> => {
      if (!cardChoices || !season) return null;
      if (readyCard) return readyCard;
      const card = await season.buildCard(cardChoices);
      cardCache.current.set(cardKey, card);
      return card;
    };
    // Every picture made already: the set is ready now, with nothing to wait for.
    const ready = set.length > 0 ? cachedShareFrames(input) : [];
    if (ready && (!cardChoices || readyCard)) {
      setBuilt({ ...madeFrom, files: readyCard ? [readyCard, ...ready] : ready });
      setProgress(null);
      return;
    }
    setProgress(null);
    // Waits for the taps to stop; a change before then cancels this set, and
    // a change while it is being made stops it before its next picture.
    const started = setTimeout(() => {
      void Promise.all([makeCard(), set.length > 0 ? buildShareFrames(input) : Promise.resolve([])])
        .then(([card, files]) => {
          if (cancelled) return;
          setBuilt({ ...madeFrom, files: card ? [card, ...files] : files });
          setProgress(null);
        })
        .catch((err) => {
          if (cancelled) return;
          reportFramesFailed(err, { layout: input.layout, size: input.size, photos: set.length });
          // A photograph that could not be fetched is a failed request, said
          // as one (the standard sentence for a failure on our side, never
          // axios's "Request failed with status code 500"); a drawing that
          // failed on the device keeps its own words.
          setBuildError({
            key: buildKey,
            message: axios.isAxiosError(err)
              ? errorMessage(err, t('plants.share.failed'))
              : err instanceof Error ? err.message : t('plants.share.failed'),
          });
          setProgress(null);
        });
    }, SHARE_REBUILD_DELAY_MS);
    return () => { cancelled = true; clearTimeout(started); };
    // The key stands in for the values rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, buildKey, formatOne, formatSpan, t]);

  /*
   * The preview is the finished frame for the picture in view, not a drawing
   * of one. The object URL is released when it is replaced, or the dialog
   * would leak a megabyte per keystroke.
   */
  /**
   * The finished picture to show: from the set for the choices as they stand,
   * or while that is being made, the last set's picture of the same photograph.
   * A photograph the last set did not have has none yet.
   */
  const previewFile = React.useMemo<File | null>(() => {
    if (!built || !shownPhoto) return null;
    if (shownIsCard) return built.cardIn ? built.files[0] ?? null : null;
    if (shownIsCollage) return built.collageIn ? built.files[built.cardIn ? 1 : 0] ?? null : null;
    const offset = (built.cardIn ? 1 : 0) + (built.collageIn ? 1 : 0);
    if (layout === 'ba' || built.layout === 'ba') return layout === built.layout ? built.files[offset] ?? null : null;
    const at = built.order.indexOf(shownPhoto.photo_id);
    return at >= 0 ? built.files[offset + at] ?? null : null;
  }, [built, layout, shownPhoto, shownIsCard, shownIsCollage]);
  /** An object URL for previewFile, released when it is replaced. */
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!previewFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(previewFile);
    setPreviewUrl(url);
    return () => { URL.revokeObjectURL(url); };
  }, [previewFile]);
  /**
   * With no picture of it yet (just tapped in), the photograph itself stands
   * in, placed as it will be cropped, so the preview follows the tap at once.
   */
  const standIn = !previewUrl && shownPhoto && !shownIsCollage
    ? shownPhoto.cardUrl || shownPhoto.signed_photo_url || shownPhoto.thumbUrl || null
    : null;
  /** What the preview shows: the finished picture, else the stand-in. */
  const previewSrc = previewUrl ?? standIn;

  return { built, buildError, progress, frames, failed, drawing, preparingShown, previewUrl, previewSrc, slotAspectOf };
}
